import { ReactNode } from 'react';
import { Box, Typography, Stack, alpha, useTheme, Breadcrumbs, Link, type SxProps, type Theme } from '@mui/material';
import { useI18n } from '../../i18n/I18nProvider';
import { translateUiText } from '../../i18n/translateUiText';

interface PageHeaderProps {
  title: string;
  subtitle?: string;
  subtitleSx?: SxProps<Theme>;
  eyebrow?: string;
  actions?: ReactNode;
  gradient?: boolean;
  breadcrumbs?: { label: string; href?: string }[];
}

/** Every admin page header is resolved through Translator Master (admin.page.* / admin.eyebrow.*). */
export default function PageHeader({
  title,
  subtitle,
  subtitleSx,
  eyebrow,
  actions,
  gradient = false,
  breadcrumbs,
}: PageHeaderProps) {
  const theme = useTheme();
  const { t } = useI18n();

  const resolvedEyebrow = eyebrow ? translateUiText(eyebrow, t) : undefined;
  const resolvedTitle = translateUiText(title, t);
  const resolvedSubtitle = subtitle ? translateUiText(subtitle, t) : undefined;

  if (gradient) {
    return (
      <Box
        sx={{
          mb: 3,
          p: { xs: 2.5, md: 3 },
          borderRadius: 3,
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          alignItems: { xs: 'stretch', md: 'center' },
          justifyContent: 'space-between',
          gap: 2,
          position: 'relative',
          overflow: 'hidden',
          background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
          boxShadow: `0 12px 40px ${alpha(theme.palette.primary.main, 0.28)}`,
          color: '#ffffff',
        }}
      >
        {/* Decorative orb */}
        <Box sx={{
          position: 'absolute',
          right: -30,
          top: -40,
          width: 180,
          height: 180,
          borderRadius: '50%',
          background: alpha('#ffffff', 0.07),
          pointerEvents: 'none',
        }} />
        <Box sx={{
          position: 'absolute',
          right: 60,
          bottom: -60,
          width: 140,
          height: 140,
          borderRadius: '50%',
          background: alpha('#ffffff', 0.05),
          pointerEvents: 'none',
        }} />
        <Box position="relative">
          {resolvedEyebrow && (
            <Typography variant="overline" sx={{ opacity: 0.85, letterSpacing: '0.1em', display: 'block', mb: 0.75 }}>
              {resolvedEyebrow}
            </Typography>
          )}
          <Typography variant="h5" fontWeight={700} sx={{ letterSpacing: '-0.03em', color: '#ffffff' }}>
            {resolvedTitle}
          </Typography>
          {resolvedSubtitle && (
            <Typography variant="body2" sx={{ mt: 0.75, opacity: 0.88, color: '#ffffff', maxWidth: 600 }}>
              {resolvedSubtitle}
            </Typography>
          )}
        </Box>
        {actions && (
          <Stack
            direction="row"
            spacing={1}
            flexWrap="wrap"
            useFlexGap
            sx={{
              width: { xs: '100%', md: 'auto' },
              '& .MuiButton-contained': {
                bgcolor: '#fff !important',
                color: `${theme.palette.primary.main} !important`,
                flex: { xs: '1 1 auto', md: '0 0 auto' },
              },
              '& .MuiButton-outlined': {
                borderColor: 'rgba(255,255,255,0.7) !important',
                color: '#fff !important',
                flex: { xs: '1 1 auto', md: '0 0 auto' },
              },
            }}
          >
            {actions}
          </Stack>
        )}
      </Box>
    );
  }

  return (
    <Box
      sx={{
        mb: 3,
        px: { xs: 0, sm: 0 },
        display: 'flex',
        flexDirection: { xs: 'column', md: 'row' },
        alignItems: { xs: 'stretch', md: 'center' },
        justifyContent: 'space-between',
        gap: 2,
      }}
    >
      <Box>
        {breadcrumbs && breadcrumbs.length > 0 && (
          <Breadcrumbs
            sx={{ mb: 1, '& .MuiBreadcrumbs-separator': { color: 'text.disabled', mx: 0.75 } }}
            aria-label="breadcrumb"
          >
            {breadcrumbs.map((crumb, i) =>
              crumb.href ? (
                <Link
                  key={i}
                  href={crumb.href}
                  underline="hover"
                  sx={{ fontSize: '0.78rem', fontWeight: 600, color: 'text.secondary' }}
                >
                  {crumb.label}
                </Link>
              ) : (
                <Typography key={i} sx={{ fontSize: '0.78rem', fontWeight: 600, color: 'text.primary' }}>
                  {crumb.label}
                </Typography>
              )
            )}
          </Breadcrumbs>
        )}
        {resolvedEyebrow && (
          <Typography
            variant="overline"
            sx={{
              display: 'block',
              mb: 0.5,
              color: theme.palette.primary.main,
              letterSpacing: '0.1em',
              fontWeight: 800,
              fontSize: '0.68rem',
            }}
          >
            {resolvedEyebrow}
          </Typography>
        )}
        <Typography
          variant="h5"
          fontWeight={700}
          sx={{
            letterSpacing: '-0.03em',
            color: 'text.primary',
          }}
        >
          {resolvedTitle}
        </Typography>
        {resolvedSubtitle && (
          <Typography
            variant="body2"
            sx={{
              mt: 0.5,
              color: 'text.secondary',
              maxWidth: 620,
              lineHeight: 1.6,
              ...subtitleSx,
            }}
          >
            {resolvedSubtitle}
          </Typography>
        )}
      </Box>
      {actions && (
        <Stack
          direction="row"
          spacing={1}
          flexWrap="wrap"
          useFlexGap
          sx={{
            width: { xs: '100%', md: 'auto' },
            '& .MuiButton-root': { flex: { xs: '1 1 auto', md: '0 0 auto' } },
          }}
        >
          {actions}
        </Stack>
      )}
    </Box>
  );
}
